import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, input, signal, viewChild, viewChildren } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDivider } from '@angular/material/divider';
import { KeyValue, UpperCasePipe } from '@angular/common';
import { filter, switchMap } from 'rxjs';

import { MetaService } from '../../meta.service';
import { BucketPipe } from "../../bucket.pipe";
import { CacheService } from '../../auth.service';
import { OfferCardComponent } from '../../offers/offer-card/offer-card.component';
import { Brand, weekdaysAsBitwise } from '../../types';
import { PlanStyleDirective } from '../../plan-style.directive';
import { ImageFallbackDirective } from '../../image-fallback.directive';
import { PaintDirective } from '../../paint.directive';
import { backend } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { JoinPipe } from '../../join.pipe';
import { GoogleMap, GoogleMapsModule, MapInfoWindow } from '@angular/google-maps';
import { ToastrService } from 'ngx-toastr';
import { HumanizePipe } from "../../humanize.pipe";

@Component({
  selector: 'brand-page',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, RouterModule, BucketPipe, OfferCardComponent, GoogleMapsModule, MatTabsModule, UpperCasePipe, JoinPipe, MatDivider, PlanStyleDirective, ImageFallbackDirective, PaintDirective, HumanizePipe],
  templateUrl: './brand-page.component.html',
  styleUrl: './brand-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export default class BrandPageComponent {
  cache = inject(CacheService);
  meta = inject(MetaService);
  route = inject(ActivatedRoute);
  http = inject(HttpClient);
  toast = inject(ToastrService);
  tab = input<string, string>('offers', { transform: (v:string) => v ?? 'offers' });
  year = new Date().getFullYear();

  LatLngBounds?: typeof google.maps.LatLngBounds;
  map = viewChild<GoogleMap>(GoogleMap);
  infoWindows = viewChildren(MapInfoWindow);

  iconSize = { width: 45, height: 45, equals: (v: any) => true };
  infoOffset = { width: 0, height: -45, equals: (v: any) => true };

  slug = input.required<string>();
  brand = signal<Brand|null>(null);

  showMore = signal<boolean>(false);

  protected tabsInfo: KeyValue<string, string>[] = [
    { key: 'offers', value: 'Offers' },
    { key: 'about', value: 'About' },
  ];

  plans = computed(() => {
    const { offers = [], plan_details = [] } = this.brand() || { offers: [], plan_details: [] };

    return plan_details.map(plan => ({
      plan: plan.identifier,
      offers: offers.filter(o => o.plan == plan.identifier)
    })).filter(group => group.offers?.length);

  });

  storeTimeslots = computed(() => {
    const stores = this.brand()?.stores || [];

    const list: { [key: string]: string[][] } = {};

    stores.forEach(store => {
      list[store._id] = [];

      for(let { key, value } of weekdaysAsBitwise){
        const dayslots: string[] = [];
        (store.days || []).filter(d => (d.day & value) == value).forEach(d => {
          dayslots.push(`${d.open || '00:00'} - ${d.close || '23:59'}`);
        });

        list[store._id].push([key, dayslots.length ? dayslots.join(', ') : 'Closed']);
      }
     });

     return list;
  });

  constructor(){
    toObservable(this.slug).pipe(
      takeUntilDestroyed(),
      filter(slug => !!slug), 
      switchMap(slug => this.http.get<Brand>(`${backend}brand?slug=${slug}`, { 
        headers: { 'no-transfer': 'true' } 
      }))
    ).subscribe({
      next: brand => {
        brand?.categories?.forEach(category => {
          this.cache.registerIconFromSVG(category.slug, category.icon_svg);
        });
        this.cache.registerPlans(brand?.plan_details??[]);
        this.brand.set(brand);
        this.meta.setTags(`${brand.name} Discounts - cloudigo Malta`, `Scroll through our ${brand.name} Discounts - Discover hundreds of incredible discounts up to 50% OFF across Malta & Gozo. Start saving now!`, brand.image_url || brand.logo_url);
      },
      error: error => {
        if(this.meta.isBrowser){
          this.toast.warning('Brand is invalid or no longer available', 'Brand not found');
        }
        this.meta.router.navigate(['/']);
      }
    });

    afterNextRender(() => {
      (google.maps.importLibrary("core") as Promise<google.maps.CoreLibrary>).then(({ LatLngBounds }: google.maps.CoreLibrary) => {
        this.LatLngBounds = LatLngBounds;
        this.centerMap();
      }).catch(err => console.error('Failed to load GoogleMapsCore'));
    });
  }

  openInfo(storeInfo: MapInfoWindow){

    const windows = this.infoWindows();

    if(windows && windows.length){
      windows.forEach(infoWindow => {
        infoWindow.close();
      });
    }
    
    storeInfo?.open();
  }

  centerMap(): void {
    const brand = this.brand();
    if(this.LatLngBounds && brand){
      const area = new this.LatLngBounds();

      const stores = brand.stores || [];

      if(stores.length == 1){
        const store = stores.at(0)!;
        setTimeout(() => {
          this.map()?.panTo({ lat: store.location.geo.y, lng: store.location.geo.x })
        });
      } else if(stores.length) {
          stores.forEach(store => {
            area.extend({ lat: store.location.geo.y, lng: store.location.geo.x });
          });
    
          setTimeout(() => {
            this.map()?.fitBounds(area, 45);
          });
      }

    }
  }

}
